import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery } from "@tanstack/react-query";

interface Update {
  id: string;
  date: string;
  text: string;
}

interface UpdatesData {
  updates: Update[];
}

interface WhatsNewDialogProps {
  lastActive: string | null;
  showUpdates: boolean;
  onDismiss: () => void;
  onToggleShowUpdates: (show: boolean) => void;
}

export function WhatsNewDialog({ lastActive, showUpdates, onDismiss, onToggleShowUpdates }: WhatsNewDialogProps) {
  const [open, setOpen] = useState(false);
  const [updates, setUpdates] = useState<Update[]>([]);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [hasBeenDismissed, setHasBeenDismissed] = useState(false);
  const {
    data: updatesData,
    isLoading: queryLoading,
    isError,
    error,
    isSuccess,
  } = useQuery<UpdatesData>({
    queryKey: ["updates"],
    enabled: showUpdates && !hasBeenDismissed,
    staleTime: 1000 * 60 * 60, // 1 hour
    retry: 1,
    queryFn: async () => {
      console.log("[WhatsNewDialog] Fetching updates.json (react-query)");
      const response = await fetch("/updates.json");
      if (!response.ok) {
        throw new Error(`Failed to fetch updates.json: ${response.status} ${response.statusText}`);
      }
      const text = await response.text();
      console.log("[WhatsNewDialog] Fetched updates.json:", text.substring(0, 100) + "...");
      let data: UpdatesData;
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        console.error("Failed to parse updates.json - please check for syntax errors:", parseError);
        throw parseError as Error;
      }
      if (!data.updates || !Array.isArray(data.updates)) {
        throw new Error("updates.json has invalid format - expected { updates: [] }");
      }
      console.log("[WhatsNewDialog] Total updates:", data.updates.length);
      return data;
    },
  });

  const filteredUpdatesMemo = useMemo<Update[]>(() => {
    if (!updatesData?.updates) return [];
    console.log("[WhatsNewDialog] lastActive:", lastActive);
    const filtered = updatesData.updates
      .filter((update) => {
        if (!lastActive) {
          console.log("[WhatsNewDialog] No lastActive, including update:", update.id);
          return true;
        }
        const updateDate = new Date(update.date + "T23:59:59Z");
        const lastActiveDate = new Date(lastActive);
        const isNewer = updateDate > lastActiveDate;
        console.log(
          "[WhatsNewDialog] Update",
          update.id,
          "date:",
          update.date,
          "vs lastActive:",
          lastActive,
          "isNewer:",
          isNewer
        );
        return isNewer;
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 10);
    console.log("[WhatsNewDialog] Filtered updates:", filtered.length);
    return filtered;
  }, [updatesData, lastActive]);

  useEffect(() => {
    if (!isSuccess) return;
    if (filteredUpdatesMemo.length === 0) {
      console.log("[WhatsNewDialog] No new updates to show");
      return;
    }

    const newest = filteredUpdatesMemo[0];
    const newestEod = new Date(newest.date + "T23:59:59Z");
    let lastSeenStr: string | null = null;
    try {
      lastSeenStr = localStorage.getItem("updates:lastSeen");
    } catch (err) {
      console.warn(
        "[WhatsNewDialog] Failed to read updates:lastSeen from localStorage; treating as not seen.",
        err
      );
    }
    if (lastSeenStr) {
      const lastSeen = new Date(lastSeenStr);
      if (lastSeen >= newestEod) {
        console.log("[WhatsNewDialog] Latest update already seen at", lastSeenStr);
        return;
      }
    }

    console.log("[WhatsNewDialog] Opening dialog with", filteredUpdatesMemo.length, "updates");
    setUpdates(filteredUpdatesMemo);
    setOpen(true);
  }, [filteredUpdatesMemo, isSuccess]);

  useEffect(() => {
    if (isError) {
      console.error("[WhatsNewDialog] Failed to load updates (react-query):", error);
    }
  }, [isError, error]);

  const handleClose = async () => {
    console.log("[WhatsNewDialog] handleClose called");
    setOpen(false);
    setHasBeenDismissed(true);

    // Persist last seen update for this browser to avoid re-showing on reloads
    try {
      const newest = updates[0];
      if (newest) {
        const newestEod = new Date(newest.date + "T23:59:59Z").toISOString();
        if (typeof window !== "undefined" && typeof window.localStorage !== "undefined") {
          localStorage.setItem("updates:lastSeen", newestEod);
          console.log("[WhatsNewDialog] Stored updates:lastSeen =", newestEod);
        } else {
          console.warn("[WhatsNewDialog] localStorage is not available; cannot persist updates:lastSeen");
        }
      }
    } catch (err) {
      console.error("[WhatsNewDialog] Failed to write updates:lastSeen to localStorage", err);
    }
    
    // If user checked "don't show again", update preference
    if (dontShowAgain) {
      await onToggleShowUpdates(false);
    }
    
    // Update lastActive when dialog is closed
    // Note: We call onDismiss which triggers updateUserLastActive in the parent
    await onDismiss();
  };

  // Don't render anything if still loading or no updates
  if (queryLoading || updates.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>What's New</DialogTitle>
          <DialogDescription>
            Here's what's changed since your last visit
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="max-h-[400px] pr-4">
          <div className="space-y-3">
            {updates.map((update) => (
              <div 
                key={update.id} 
                className="flex gap-3 p-3 rounded-lg border bg-muted/40"
              >
                <div className="flex-shrink-0 text-sm text-muted-foreground font-medium min-w-[100px]">
                  {new Date(update.date).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
                <div className="flex-1 text-sm">
                  {update.text}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-col gap-4">
          <div className="flex items-start space-x-2">
            <Checkbox
              id="dont-show"
              checked={dontShowAgain}
              onCheckedChange={(checked) => setDontShowAgain(checked === true)}
            />
            <div className="space-y-1">
              <Label
                htmlFor="dont-show"
                className="text-sm font-normal cursor-pointer"
              >
                Never show any update nitifications
              </Label>
              <p className="text-xs text-muted-foreground leading-snug">
                (You can change this in your Account Settings)
              </p>
            </div>
          </div>
          
          <Button onClick={handleClose} className="w-full sm:w-auto">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
